'use client';

import { useMemo } from 'react';
import { useQuery } from '@apollo/client/react';
import { format } from 'date-fns';
import {
  INVOICE_TOTALS_QUERY,
  type InvoiceTotals,
  type DocumentFilterInput,
} from '@paeffgen/react/crm-documents/data-access-graphql';
import { useAdmin } from '@paeffgen/react/crm-documents/feature-auth';
import { Skeleton } from '@paeffgen/react/crm-documents/ui';
import { formatCurrency } from '@paeffgen/react/crm-documents/util';

/**
 * Zeitraum-Text für die Zähler-Zeile: „01.01. – 08.07.2026" (Jahr am Start
 * entfällt, wenn es dem End-Jahr entspricht). Offenes Ende = heute; ganz ohne
 * Datumsfilter „gesamter Zeitraum".
 */
function rangeLabel(dateFrom?: string, dateTo?: string): string {
  if (!dateFrom && !dateTo) return 'gesamter Zeitraum';
  const end = dateTo ? new Date(dateTo) : new Date();
  if (!dateFrom) return `bis ${format(end, 'dd.MM.yyyy')}`;
  const from = new Date(dateFrom);
  const fromPattern =
    from.getFullYear() === end.getFullYear() ? 'dd.MM.' : 'dd.MM.yyyy';
  return `${format(from, fromPattern)} – ${format(end, 'dd.MM.yyyy')}`;
}

/**
 * Kompakte „Rechnungssumme (netto)"-Card der Beleg-Liste — Layout nach der
 * Claude-Design-Vorlage „Rechnungssumme Card / Belegseite (gefiltert)":
 * rechtsbündige 480px-Card mit orangem Links-Akzent, Label + Zähler/Zeitraum
 * links, Betrag rechts, Legal-Hinweis darunter.
 *
 * Aggregiert wird serverseitig über ALLE Belege des aktuellen Filters (die
 * Liste ist paginiert, eine Summe der sichtbaren Zeilen wäre falsch). Der
 * Typ-Filter der Liste wird bewusst NICHT übernommen: die Kennzahl ist immer
 * Rechnungen − Gutschriften, damit sie stets dasselbe bedeutet — auch wenn
 * die Liste gerade z. B. nur Lieferscheine zeigt.
 *
 * Nur im Einzel-Debitor-Kontext sichtbar (Aggregat über fremde/mehrere
 * Debitoren wäre irreführend — gleiches Gating wie Favoriten/Materialanfrage).
 */
export function InvoiceTotalsBar({ filter }: { filter: DocumentFilterInput }) {
  const { adminQueryVars, isSingleCustomerContext } = useAdmin();

  // types raus, damit der Apollo-Cache nicht pro Typ-Filter fragmentiert
  // (der Server ignoriert types ohnehin)
  const totalsFilter = useMemo(() => {
    const { types: _types, ...rest } = filter;
    return rest;
  }, [filter]);

  const { data, loading, error } = useQuery<{ invoiceTotals: InvoiceTotals }>(
    INVOICE_TOTALS_QUERY,
    {
      variables: { filter: totalsFilter, ...adminQueryVars },
      fetchPolicy: 'cache-and-network',
      skip: !isSingleCustomerContext,
    },
  );

  if (!isSingleCustomerContext) return null;
  // Komfort-Kennzahl, kein Kerninhalt: bei Fehler still ausblenden —
  // die Liste selbst hat ihre eigene Fehleranzeige.
  if (error) return null;

  const totals = data?.invoiceTotals;

  return (
    <div className="w-full md:w-[480px] md:shrink-0 md:ml-auto bg-white border border-zinc-200 border-l-[3px] border-l-primary rounded-xl shadow-[0_1px_2px_rgba(0,0,0,0.03),0_4px_12px_rgba(0,0,0,0.025)] px-5 py-4 md:px-[22px] md:py-[18px]">
      {!totals && loading ? (
        <div className="space-y-2">
          <Skeleton className="h-4 w-56" />
          <Skeleton className="h-7 w-40" />
        </div>
      ) : totals ? (
        <>
          {/* Mobil gestapelt (Label → Betrag → Zähler); ab md zweispaltig:
              links Label + Zähler, rechts der Betrag — nebeneinander gequetscht
              reicht die Breite auf dem Handy nicht. */}
          <div className="md:grid md:grid-cols-[minmax(0,1fr)_auto] md:gap-x-5">
            <p className="text-[11.5px] font-bold uppercase tracking-[0.05em] text-zinc-500 md:col-start-1 md:row-start-1">
              Rechnungssumme (netto) · gewählter Zeitraum
            </p>
            <p className="mt-1.5 md:mt-0 text-3xl font-extrabold leading-none tracking-tight tabular-nums text-secondary whitespace-nowrap md:col-start-2 md:row-start-1 md:row-span-2 md:justify-self-end md:self-start">
              {formatCurrency(totals.netSum)}
            </p>
            <p className="mt-1.5 md:mt-1 text-sm text-zinc-500 md:col-start-1 md:row-start-2">
              <b className="font-bold text-secondary tabular-nums">
                {totals.invoiceCount}
              </b>{' '}
              {totals.invoiceCount === 1 ? 'Rechnung' : 'Rechnungen'}
              {totals.creditCount > 0 && (
                <>
                  {' '}
                  −{' '}
                  <b className="font-bold text-secondary tabular-nums">
                    {totals.creditCount}
                  </b>{' '}
                  {totals.creditCount === 1 ? 'Gutschrift' : 'Gutschriften'} (
                  {formatCurrency(totals.netCreditTotal)})
                </>
              )}{' '}
              · {rangeLabel(totalsFilter.dateFrom, totalsFilter.dateTo)}
            </p>
          </div>
          <p className="mt-3.5 pt-3 border-t border-zinc-100 text-[11px] leading-normal text-zinc-400">
            Unverbindliche Übersicht auf Basis der im Kundenportal verfügbaren
            Belege — keine buchhalterische oder steuerliche Auswertung,
            maßgeblich sind die Originalbelege.
          </p>
        </>
      ) : null}
    </div>
  );
}
